import React, { useEffect, useState } from "react";
import logoFull from "../../../assets/images/cardly_logo_white.png";
import logoIcon from "../../../assets/images/cardly_logo_icon_white.png";
import { useAuth } from "context/auth";
import { useNavigate, useLocation } from "react-router-dom";
import {
  BarChart3,
  Building2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  LayoutDashboard,
  LogOut,
  Menu,
  Plus,
  Settings,
  Shield,
  Sparkles,
  Tickets,
  UserCircle2,
  Users,
  X,
} from "lucide-react";

const SidebarItem = ({
  icon,
  label,
  onClick,
  collapsed,
  danger,
  active,
  isChild = false,
}) => (
  <button
    onClick={onClick}
    className={`
      tw-w-full tw-flex tw-items-center
      ${collapsed ? "tw-justify-center" : "tw-gap-3"}
      ${isChild ? "tw-pl-4" : ""}
      tw-px-3 tw-py-3 tw-text-sm tw-transition-all tw-rounded-2xl
      ${
        active
          ? "tw-bg-white/12 tw-text-white tw-shadow-[0_10px_24px_rgba(15,23,42,0.08)]"
          : danger
          ? "tw-text-[#F35658]"
          : "tw-text-white hover:tw-bg-white/10"
      }
    `}
  >
    <span className={isChild ? "tw-scale-90" : ""}>{icon}</span>
    {!collapsed && <span>{label}</span>}
  </button>
);

const SidebarDropdown = ({ icon, label, children, collapsed, isActive }) => {
  const [open, setOpen] = useState(isActive);

  useEffect(() => {
    setOpen(isActive);
  }, [isActive]);

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className={`
          tw-w-full tw-relative tw-flex tw-items-center
          ${collapsed ? "tw-justify-center" : "tw-gap-3"}
          tw-px-3 tw-py-3 tw-text-sm tw-rounded-2xl tw-transition-all
          ${
            isActive
              ? "tw-bg-white/12 tw-text-white tw-shadow-[0_10px_24px_rgba(15,23,42,0.08)]"
              : "tw-text-white hover:tw-bg-white/10"
          }
        `}
      >
        <span>{icon}</span>
        {!collapsed && <span>{label}</span>}
        {!collapsed && (
          <span className="tw-absolute tw-right-3 tw-text-white/45">
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
        )}
      </button>

      {!collapsed && open && (
        <div className="tw-ml-5 tw-mt-2 tw-space-y-1 tw-border-l tw-border-white/10 tw-pl-4">
          {children}
        </div>
      )}
    </div>
  );
};

const SidebarSection = ({ title, collapsed }) =>
  collapsed ? null : (
    <h4 className="tw-mt-7 tw-mb-2 tw-px-2 tw-text-[10px] tw-uppercase tw-tracking-[0.24em] tw-text-white/35 tw-font-semibold">
      {title}
    </h4>
  );

const MENU = [
  { icon: <LayoutDashboard size={18} />, label: "Dashboard", path: "/admin-v2" },
  { section: "Manage Cardly Cards" },
  {
    icon: <Users size={18} />,
    label: "Cardholders",
    children: [
      { icon: <Users size={16} />, label: "View Cardholders", path: "/admin-v2/cardholders" },
      { icon: <Sparkles size={16} />, label: "Create Pass", path: "/admin-v2/cardholders/create" },
    ],
  },
  
  {
    icon: <Building2 size={18} />,
    label: "Enterprise Accounts",
    children: [
      { icon: <Building2 size={16} />, label: "View Enterprises", path: "/admin-v2/enterprises" },
      { icon: <Plus size={16} />, label: "Create Enterprise", path: "/admin-v2/enterprises", search: "?create=1" },
    ],
  },
  {
    icon: <Building2 size={18} />,
    label: "Organization Accounts",
    children: [
      { icon: <Building2 size={16} />, label: "View Organizations", path: "/admin-v2/organizations" },
    ],
  },
  {
    icon: <UserCircle2 size={18} />,
    label: "Customers",
    children: [
      { icon: <UserCircle2 size={16} />, label: "View Customers", path: "/admin-v2/customers" },
    ],
  },
  { icon: <Tickets size={18} />, label: "Vouchers", path: "/admin-v2/vouchers" },
  { section: "Business & Billing" },
  { icon: <CreditCard size={18} />, label: "Plans", path: "/admin-v2/plans" },
  { icon: <Sparkles size={18} />, label: "Subscriptions", path: "/admin-v2/subscriptions" },
  { icon: <BarChart3 size={18} />, label: "Reports", path: "/admin-v2/reports" },
  { section: "System" },
  { icon: <Shield size={18} />, label: "Admin Management", path: "/admin-v2/admins" },
  { icon: <Settings size={18} />, label: "System Settings", path: "/admin-v2/settings" },
  { icon: <BarChart3 size={18} />, label: "Audit Logs", path: "/admin-v2/audit-logs" },
];

export default function AdminV2Sidebar({ collapsed = false, onToggleCollapse }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const backToLegacy = () => {
    localStorage.removeItem("admin_ui");
    navigate("/", { replace: true });
  };

  const renderMenu = (isCollapsed = false, closeMobile = false) =>
    MENU.map((item, idx) => {
      if (item.section) {
        return <SidebarSection key={idx} title={item.section} collapsed={isCollapsed} />;
      }

      if (item.children) {
        const isActive = item.children.some((child) =>
          location.pathname.startsWith(child.path)
        );

        return (
          <SidebarDropdown
            key={idx}
            icon={item.icon}
            label={item.label}
            collapsed={isCollapsed}
            isActive={isActive}
          >
            {item.children.map((child, cidx) => (
              <SidebarItem
                key={cidx}
                icon={child.icon}
                label={child.label}
                isChild
                active={
                  location.pathname === child.path &&
                  (!child.search || location.search === child.search)
                }
                onClick={() => {
                  if (closeMobile) setMobileOpen(false);
                  navigate(`${child.path}${child.search || ""}`);
                }}
              />
            ))}
          </SidebarDropdown>
        );
      }

      return (
        <SidebarItem
          key={idx}
          icon={item.icon}
          label={item.label}
          collapsed={isCollapsed}
          active={location.pathname === item.path}
          onClick={() => {
            if (closeMobile) setMobileOpen(false);
            navigate(item.path);
          }}
        />
      );
    });

  return (
    <>
      <div className="lg:tw-hidden tw-sticky tw-top-0 tw-z-30 tw-border-b tw-border-[rgba(31,41,64,0.08)] tw-bg-white/92 tw-backdrop-blur-md">
        <div className="admin-v2-app-fluid tw-flex tw-items-center tw-justify-between tw-py-3">
          <button onClick={() => setMobileOpen(true)} className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white">
            <Menu size={18} />
          </button>
          <img src={logoFull} alt="Cardly Logo" className="tw-h-7" />
          <div className="tw-h-10 tw-w-10" />
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:tw-hidden tw-fixed tw-inset-0 tw-z-50 tw-bg-[rgba(15,23,42,0.42)] tw-backdrop-blur-[2px]">
          <div className="tw-flex tw-h-full tw-w-[min(88vw,340px)] tw-flex-col tw-bg-[var(--admin-v2-sidebar)] tw-p-4 tw-text-white tw-shadow-[0_24px_70px_rgba(20,26,44,0.36)]">
            <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between">
              <img src={logoFull} alt="Cardly Logo" className="tw-h-7 tw-brightness-[3]" />
              <button className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-white/12 tw-bg-white/10" onClick={() => setMobileOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="tw-mb-4 tw-rounded-[24px] tw-border tw-border-white/10 tw-bg-white/8 tw-p-4">
              <p className="tw-text-[11px] tw-uppercase tw-tracking-[0.22em] tw-text-white/45">Cardly admin</p>
              <p className="tw-mt-2 tw-text-lg tw-font-semibold tw-leading-tight">Mobile-ready control for workspaces, vouchers, subscriptions, and audits.</p>
            </div>
            <div className="admin-v2-sidebar-scroll tw-flex-1 tw-overflow-y-auto tw-pr-1">
              {renderMenu(false, true)}
            </div>
            <div className="tw-mt-6 tw-border-t tw-border-white/10 tw-pt-3">
              <SidebarItem icon={<UserCircle2 size={18} />} label="My Account" onClick={() => { setMobileOpen(false); navigate("/admin-v2/account"); }} />
              <SidebarItem icon={<ChevronLeft size={18} />} label="Back to Legacy" onClick={backToLegacy} />
              <SidebarItem icon={<LogOut size={18} />} label="Logout" danger onClick={logout} />
            </div>
          </div>
          <button
            type="button"
            className="tw-h-full tw-flex-1"
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
          />
        </div>
      )}

      <div
        className={`
          tw-hidden lg:tw-flex tw-fixed tw-left-0 tw-top-0 tw-h-screen
          ${collapsed ? "tw-w-[88px]" : "tw-w-[300px] xl:tw-w-[300px]"}
          tw-bg-[var(--admin-v2-sidebar)] tw-p-4 tw-pt-6 tw-flex-col tw-transition-all tw-duration-300
          tw-rounded-none tw-rounded-r-[30px] tw-shadow-[0_25px_70px_rgba(20,26,44,0.18)] tw-relative
        `}
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-5 tw-h-10">
          <img
            src={collapsed ? logoIcon : logoFull}
            alt="Cardly Logo"
            className="tw-h-full tw-w-auto tw-object-contain"
          />
          {!collapsed && (
            <div className="tw-rounded-full tw-bg-white/10 tw-px-3 tw-py-1 tw-text-[11px] tw-uppercase tw-tracking-[0.2em] tw-text-[#FAD845]">
              v2.0
            </div>
          )}
        </div>

        {!collapsed && (
          <div className="tw-mb-4 tw-rounded-[24px] tw-bg-white/8 tw-p-4 tw-text-white">
            <p className="tw-text-[11px] tw-uppercase tw-tracking-[0.22em] tw-text-white/45">Admin Workspace</p>
            {/* <p className="tw-mt-2 tw-text-xl tw-font-semibold tw-leading-tight">Cleaner control for cards, teams, and brand flows.</p> */}
          </div>
        )}

        <div className="admin-v2-sidebar-scroll tw-flex-1 tw-overflow-y-auto tw-pr-1">{renderMenu(collapsed)}</div>

        <div className="tw-border-t tw-border-white/10 tw-pt-3 tw-space-y-1">
          <SidebarItem icon={<UserCircle2 size={18} />} label="My Account" collapsed={collapsed} onClick={() => navigate("/admin-v2/account")} />
          <SidebarItem icon={<ChevronLeft size={18} />} label="Back to Legacy" collapsed={collapsed} onClick={backToLegacy} />
          <SidebarItem icon={<LogOut size={18} />} label="Logout" collapsed={collapsed} danger onClick={logout} />
        </div>

        <button
          onClick={() => onToggleCollapse?.()}
          className="tw-absolute tw-top-1/2 tw-right-[-12px] -tw-translate-y-1/2 tw-flex tw-h-12 tw-w-6 tw-items-center tw-justify-center tw-rounded-r-lg tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-shadow-sm hover:tw-bg-[rgba(44,50,85,0.04)]"
        >
          <span className="admin-v2-text-muted">{collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}</span>
        </button>
      </div>
    </>
  );
}
